
import { defineComponent } from 'vue';
import { mapState, mapWritableState } from 'pinia';
import { useStore } from '@/plugins/store/index';
import CustomSelect from '@/components/ui/CustomSelect.vue';
import CustomMultiSelect from '@/components/ui/CustomMultiSelect.vue';
import Modal from '@/components/ui/Modal.vue';
import { AllFilterSelectors, Region, TimePeriod, genderTranslation } from '@/types';

export default defineComponent({
    components: { CustomSelect, CustomMultiSelect, Modal },
    props: {
        includeCourses: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            selectAllRegions: AllFilterSelectors.AllRegions,
            sticky: false,
            tempCustomFrom: '',
            tempCustomTo: '',
            lastNonCustomPeriod: null as any,
            skipCustomRangeModal: false,
        };
    },
    mounted() {
        this.handleScroll();
        window.addEventListener('scroll', this.handleScroll);
        window.addEventListener('resize', this.handleScroll);
    },
    beforeUnmount() {
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.handleScroll);
    },
    watch: {
        selectedPeriod: {
            handler(newValue, oldValue) {
                if (newValue?.id === TimePeriod.CustomRange) {
                    if (!this.skipCustomRangeModal) {
                        this.openCustomRangeModal(oldValue);
                    }
                    if (this.customDateRange.from && this.customDateRange.to) {
                        const label = this.formatCustomRangeLabel(this.customDateRange.from, this.customDateRange.to);
                        if (this.selectedPeriod?.text !== label) {
                            this.skipCustomRangeModal = true;
                            this.selectedPeriod = { id: TimePeriod.CustomRange, text: label };
                            return;
                        }
                    }
                    this.skipCustomRangeModal = false;
                } else if (newValue) {
                    this.lastNonCustomPeriod = newValue;
                }
            },
            deep: false,
        },
    },
    methods: {
        handleScroll() {
            this.sticky = window.scrollY > 163 && window.innerWidth > 1024;
        },
        openCustomRangeModal(previousPeriod?: any) {
            this.tempCustomFrom = this.customDateRange.from || '';
            this.tempCustomTo = this.customDateRange.to || '';
            if (previousPeriod && previousPeriod.id !== TimePeriod.CustomRange) {
                this.lastNonCustomPeriod = previousPeriod;
            }
            (this.$refs.customRangeModal as any)?.open();
        },
        onModalClose() {
            if (this.selectedPeriod?.id === TimePeriod.CustomRange && (!this.customDateRange.from || !this.customDateRange.to)) {
                this.skipCustomRangeModal = true;
                this.selectedPeriod = this.lastNonCustomPeriod || this.timePeriods?.[0] || null;
            }
        },
        cancelCustomRange() {
            (this.$refs.customRangeModal as any)?.close();
            this.onModalClose();
        },
        formatDate(dateString: string) {
            const [year, month, day] = dateString.split('-');
            if (!year || !month || !day) {
                return dateString;
            }
            return `${day}-${month}-${year}`;
        },
        formatCustomRangeLabel(from: string, to: string) {
            return `${this.formatDate(from)} - ${this.formatDate(to)}`;
        },
        applyCustomRange() {
            const fromDate = this.tempCustomFrom ? new Date(this.tempCustomFrom) : null;
            const toDate = this.tempCustomTo ? new Date(this.tempCustomTo) : null;
            if (
                !fromDate ||
                !toDate ||
                Number.isNaN(fromDate.getTime()) ||
                Number.isNaN(toDate.getTime()) ||
                fromDate.getFullYear() < 1000 ||
                toDate.getFullYear() < 1000 ||
                toDate < fromDate
            ) {
                return;
            }
            this.customDateRange = { from: this.tempCustomFrom, to: this.tempCustomTo };
            this.skipCustomRangeModal = true;
            this.selectedPeriod = { id: TimePeriod.CustomRange, text: this.formatCustomRangeLabel(this.tempCustomFrom, this.tempCustomTo) };
            (this.$refs.customRangeModal as any)?.close();
        },
    },
    computed: {
        ...mapState(useStore, ['courses', 'regions', 'ageRanges', 'timePeriods', 'genders']),
        ...mapWritableState(useStore, ['selectedPeriod', 'selectedAgeRange', 'selectedCourse', 'selectedRegions', 'selectedGender', 'customDateRange']),
        gendersWithTranslations(): any {
            return (
                this.genders?.map((currentGender) => {
                return { ...currentGender, translatedText: (genderTranslation as any)[currentGender.id] || currentGender.text };
            }) || []
            );
        },
        selectableRegions(): Region[] | undefined {
            return this.regions?.filter((currentRegion) => currentRegion.excludeFromSorting !== true);
        },
    },
});
